import { useEffect, useState } from "react";
import { CreditCard, Copy, ExternalLink, RefreshCw, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

const statusColor = {
  created: "bg-[#D99B41]",
  paid: "bg-[#477A5D]",
  captured: "bg-[#477A5D]",
  "payment_link.paid": "bg-[#477A5D]",
  expired: "bg-[#8A7970]",
  cancelled: "bg-[#8A7970]",
  failed: "bg-[#B84F3E]",
};

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", guest_name: "", guest_phone: "", description: "" });
  const [creating, setCreating] = useState(false);

  const load = () => {
    api.get("/payments").then(r => setPayments(r.data)).catch(() => {});
    api.get("/payments/test-razorpay").then(r => setStatus(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const create = async () => {
    if (!form.amount || !form.guest_name || !form.guest_phone) {
      toast.error("Amount, name, phone required");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post("/payment/create-link", { ...form, amount: parseInt(form.amount) });
      toast.success(`Link created (${r.data.mode})`);
      setOpen(false);
      setForm({ amount: "", guest_name: "", guest_phone: "", description: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setCreating(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-4xl">Payments</h1>
          <p className="text-[#8A7970] mt-1">All payment links generated by the AI bot & manually.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-full bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="new-payment-btn">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5}/> Create Link
        </Button>
      </div>

      {/* Razorpay status */}
      <div className="mb-6 bg-[#FDFBF7] rounded-xl border border-[#E7C2A6]/40 p-5" data-testid="razorpay-status">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-5 h-5 text-[#B84F3E]" strokeWidth={1.5}/>
              <h3 className="font-serif text-xl">Razorpay Status</h3>
            </div>
            {status?.configured ? (
              status.error ? (
                <p className="text-sm text-[#B84F3E]">❌ Keys set but connection failed: {status.error}</p>
              ) : (
                <p className="text-sm text-[#477A5D]">✅ Connected · Mode: <b>{status.mode}</b> · Key: {status.key_id_preview}</p>
              )
            ) : (
              <div>
                <p className="text-sm text-[#D99B41]">⚠️ Razorpay keys not configured — using UPI intent fallback.</p>
                <p className="text-xs text-[#8A7970] mt-1">To enable Razorpay, add <code className="bg-[#F5F2EB] px-1.5 py-0.5 rounded">RAZORPAY_KEY_ID</code>, <code className="bg-[#F5F2EB] px-1.5 py-0.5 rounded">RAZORPAY_KEY_SECRET</code>, <code className="bg-[#F5F2EB] px-1.5 py-0.5 rounded">RAZORPAY_WEBHOOK_SECRET</code> to backend .env then restart.</p>
              </div>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="reload-btn"><RefreshCw className="w-3.5 h-3.5"/></Button>
        </div>
      </div>

      {/* Payments list */}
      <div className="bg-[#FDFBF7] rounded-xl border border-[#E7C2A6]/40 overflow-hidden">
        {payments.length === 0 ? (
          <div className="p-10 text-center text-[#8A7970]">No payment links yet. Once the AI bot confirms a booking, links will appear here.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F5F2EB] text-[#8A7970]">
              <tr>
                <th className="text-left p-3 font-medium">Guest</th>
                <th className="text-left p-3 font-medium">Amount</th>
                <th className="text-left p-3 font-medium">Provider</th>
                <th className="text-left p-3 font-medium">Status</th>
                <th className="text-left p-3 font-medium">Created</th>
                <th className="text-left p-3 font-medium">Link</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.id} className="border-t border-[#E7C2A6]/30" data-testid={`payment-row-${p.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{p.guest_name}</div>
                    <div className="text-xs text-[#8A7970]">{p.guest_phone}</div>
                  </td>
                  <td className="p-3 font-serif text-[#B84F3E]">₹{p.amount?.toLocaleString('en-IN')}</td>
                  <td className="p-3 text-xs capitalize">{p.provider === 'razorpay' ? '💳 Razorpay' : '📱 UPI Intent'}</td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-white text-xs capitalize ${statusColor[p.status] || "bg-[#8A7970]"}`}>{p.status}</span>
                  </td>
                  <td className="p-3 text-xs text-[#8A7970]">{new Date(p.created_at).toLocaleString('en-IN')}</td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => copy(p.link)} data-testid={`copy-${p.id}`}><Copy className="w-3 h-3"/></Button>
                      {p.provider === 'razorpay' && <a href={p.link} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><ExternalLink className="w-3 h-3"/></Button></a>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-payment-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">New Payment Link</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-xs text-[#8A7970]">Amount (₹)</label><Input type="number" value={form.amount} onChange={(e)=>setForm({...form,amount:e.target.value})} data-testid="pay-amount-input"/></div>
            <div><label className="text-xs text-[#8A7970]">Guest Name</label><Input value={form.guest_name} onChange={(e)=>setForm({...form,guest_name:e.target.value})} data-testid="pay-name-input"/></div>
            <div><label className="text-xs text-[#8A7970]">Guest Phone</label><Input value={form.guest_phone} onChange={(e)=>setForm({...form,guest_phone:e.target.value})} placeholder="919876543210" data-testid="pay-phone-input"/></div>
            <div><label className="text-xs text-[#8A7970]">Description</label><Input value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} placeholder="e.g., 50% advance for 2 nights"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={creating} className="bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="pay-create-btn">
              {creating ? "Creating..." : "Create Link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
